import React, { useState, useEffect, useRef } from 'react';
import { addDriver, getCongregations } from '../services/firebase';
import './Forms.css';

const DAYS = [
  { key: 'thu', label: 'Thu - April 9, 2026' },
  { key: 'fri', label: 'Fri - April 10, 2026' },
  { key: 'sat', label: 'Sat - April 11, 2026' },
  { key: 'sun', label: 'Sun - April 12, 2026' }
];

const AVAILABILITY_OPTIONS = [
  'Anytime',
  'Morning only (ending before noon)',
  'Afternoon only (ending before 5PM)',
  'Evening only (ending by 9PM)',
  'Not Available'
];

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  congregation: '',
  thuAvailability: '',
  friAvailability: '',
  satAvailability: '',
  sunAvailability: '',
  vehicleType: '',
  passengerCapacity: '',
  cartCapacity: '',
  alsoDoingCartShifts: ''
};

function DriverForm() {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [congregations, setCongregations] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const errorRef = useRef(null);

  useEffect(() => {
    getCongregations().then(data => {
      const active = data.filter(c => c.active).map(c => c.name).sort();
      setCongregations(active);
    });
  }, []);

  const showError = (msg) => {
    setError(msg);
    // Scroll to the error message
    setTimeout(() => {
      if (errorRef.current) {
        errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 50);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.firstName || !formData.lastName || !formData.phone || !formData.email || !formData.congregation) {
      showError('Please fill in all required fields.');
      setLoading(false);
      return;
    }

    if (!formData.thuAvailability || !formData.friAvailability ||
        !formData.satAvailability || !formData.sunAvailability) {
      showError('Please indicate your availability for all days.');
      setLoading(false);
      return;
    }

    if (!formData.vehicleType || !formData.passengerCapacity || !formData.cartCapacity) {
      showError('Please fill in all vehicle information.');
      setLoading(false);
      return;
    }

    try {
      const result = await addDriver(formData);
      if (result.success) {
        setSubmitted(true);
        setFormData(EMPTY_FORM);
      } else {
        showError(`Submission failed: ${result.error || 'Please try again. Check that the app has permission to save data.'}`);
      }
    } catch (err) {
      showError(`An error occurred: ${err.message || 'Please try again.'}`);
      console.error('DriverForm submit error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="form-container">
        <div className="success-message">
          <h2>Thank You for Registering as a Driver!</h2>
          <p>Your driver information has been submitted successfully.</p>
          <p>The shuttling coordinator will contact you with your driving assignments before the event.</p>
          <p>Please ensure your vehicle is clean, in good working order, and has adequate insurance coverage.</p>
          <button onClick={() => setSubmitted(false)} className="btn btn-primary">
            Submit Another Response
          </button>
          <a href="/" className="btn btn-secondary">Return to Home</a>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <div className="form-header">
        <h1>SMPW - 2026 Masters Event</h1>
        <h2>Driver/Shuttler Registration</h2>
        <p>Thank you for volunteering to serve as a shuttler/driver during the 2026 Masters Event. Drivers will transport cart volunteers from the Kingdom Hall to their assigned locations and pick them up when their shifts end. Please complete this form by <strong>March 15, 2026</strong>.</p>
      </div>

      <form onSubmit={handleSubmit} className="volunteer-form">
        <div className="form-section">
          <h3>Personal Information</h3>

          <div className="form-group">
            <label htmlFor="firstName">First Name *</label>
            <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required />
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Last Name *</label>
            <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number *</label>
            <input type="tel" id="phone" name="phone" placeholder="706-555-1234" value={formData.phone} onChange={handleChange} required />
            <small>Please include area code</small>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required />
          </div>

          <div className="form-group">
            <label htmlFor="congregation">Congregation *</label>
            <select id="congregation" name="congregation" value={formData.congregation} onChange={handleChange} required>
              <option value="">Select congregation...</option>
              {congregations.map(cong => (
                <option key={cong} value={cong}>{cong}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-section">
          <h3>Availability for Shuttling</h3>
          <p className="instruction">Please indicate when you're available to drive. You may be assigned to drop-off runs (before shifts start) and/or pick-up runs (after shifts end).</p>

          <div className="availability-grid">
            <table>
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Availability</th>
                </tr>
              </thead>
              <tbody>
                {DAYS.map(day => (
                  <tr key={day.key}>
                    <td className="day-label">{day.label}</td>
                    <td>
                      <select
                        name={`${day.key}Availability`}
                        value={formData[`${day.key}Availability`]}
                        onChange={handleChange}
                        required
                        className="availability-select"
                      >
                        <option value="">Select...</option>
                        {AVAILABILITY_OPTIONS.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="form-section">
          <h3>Vehicle Information</h3>

          <div className="form-group">
            <label htmlFor="vehicleType">Vehicle Type *</label>
            <select id="vehicleType" name="vehicleType" value={formData.vehicleType} onChange={handleChange} required>
              <option value="">Select...</option>
              <option value="SUV">SUV</option>
              <option value="Minivan">Minivan</option>
              <option value="Van">Van</option>
              <option value="Sedan">Sedan</option>
              <option value="Hatchback">Hatchback</option>
              <option value="Truck / Pickup">Truck / Pickup</option>
              <option value="Crossover">Crossover</option>
              <option value="Wagon">Wagon</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="passengerCapacity">Passenger Capacity *</label>
            <input type="number" id="passengerCapacity" name="passengerCapacity" min="1" max="15" value={formData.passengerCapacity} onChange={handleChange} required />
            <small>How many passengers can your vehicle hold (NOT including the driver)? Example: 4</small>
          </div>

          <div className="form-group">
            <label htmlFor="cartCapacity">Cart Capacity *</label>
            <input type="number" id="cartCapacity" name="cartCapacity" min="0" max="10" value={formData.cartCapacity} onChange={handleChange} required />
            <small>How many literature carts can fit in your trunk/cargo area? Example: 2</small>
          </div>

          <div className="form-group">
            <label htmlFor="alsoDoingCartShifts">Are you also volunteering for cart witnessing shifts?</label>
            <select id="alsoDoingCartShifts" name="alsoDoingCartShifts" value={formData.alsoDoingCartShifts} onChange={handleChange}>
              <option value="">Select...</option>
              <option value="Yes">Yes, I'm also signed up for cart shifts</option>
              <option value="No">No, I'm only available for shuttling</option>
            </select>
            <small>This helps us avoid scheduling you to drive during your own cart shift</small>
          </div>
        </div>

        {/* Error shown near submit button so it's always visible */}
        {error && (
          <div ref={errorRef} className="error-message" style={{ marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Submitting...' : 'Submit Registration'}
          </button>
          <a href="/" className="btn btn-secondary">Cancel</a>
        </div>
      </form>
    </div>
  );
}

export default DriverForm;
